import { Color, type ExcaliburGraphicsContext, vec } from "excalibur";
import type { ColonyRuntime } from "../colony/colony-runtime";
import {
  CellCoordComponent,
  CellStateComponent,
} from "../colony/ecs/components/colony-components";
import { COLONY } from "../colony/constants";
import { eligibleFoundationCoordsForLevel } from "../colony/placement";
import { hiveKey, parseHiveKey, type HiveCoord } from "../grid/hive-levels";
import { hexToWorld } from "../grid/hex-grid";
import { cellSpritesImage } from "../resources";

const hexCorners = (cx: number, cy: number, r: number): [number, number][] => {
  const pts: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 6 + (i * Math.PI) / 3;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
};

/** Ring radius as a fraction of hex size (outside the inner cell disc). */
const BUILD_RING_RADIUS_FACTOR = 0.68;
const BUILD_RING_SEGMENTS = 48;

/**
 * Draws a track ring and a progress arc (0–1) for foundation cells under construction.
 */
const drawBuildProgressRing = (
  ctx: ExcaliburGraphicsContext,
  center: { x: number; y: number },
  radius: number,
  progress: number,
): void => {
  const p = Math.min(1, Math.max(0, progress));
  const start = -Math.PI / 2;
  const track = Color.fromHex("#34495e");
  const fill = Color.fromHex("#f1c40f");
  const trackW = 2;
  const progW = 3.5;
  for (let i = 0; i < BUILD_RING_SEGMENTS; i++) {
    const a0 = start + (i / BUILD_RING_SEGMENTS) * Math.PI * 2;
    const a1 = start + ((i + 1) / BUILD_RING_SEGMENTS) * Math.PI * 2;
    ctx.drawLine(
      vec(center.x + radius * Math.cos(a0), center.y + radius * Math.sin(a0)),
      vec(center.x + radius * Math.cos(a1), center.y + radius * Math.sin(a1)),
      track,
      trackW,
    );
  }
  if (p <= 0) {
    return;
  }
  const progSegs = Math.max(2, Math.ceil(BUILD_RING_SEGMENTS * p));
  const sweep = p * Math.PI * 2;
  for (let i = 0; i < progSegs; i++) {
    const a0 = start + (i / progSegs) * sweep;
    const a1 = start + ((i + 1) / progSegs) * sweep;
    ctx.drawLine(
      vec(center.x + radius * Math.cos(a0), center.y + radius * Math.sin(a0)),
      vec(center.x + radius * Math.cos(a1), center.y + radius * Math.sin(a1)),
      fill,
      progW,
    );
  }
};

/**
 * Progress ring for brood egg (time to hatch) and larvae (feeding toward sealing).
 * Uses distinct palettes from the foundation build ring.
 */
const drawBroodGrowthRing = (
  ctx: ExcaliburGraphicsContext,
  center: { x: number; y: number },
  radius: number,
  progress: number,
  variant: "egg" | "larvae",
): void => {
  const p = Math.min(1, Math.max(0, progress));
  const track = Color.fromHex(variant === "egg" ? "#d5d8dc" : "#b7950b");
  const fill = Color.fromHex(variant === "egg" ? "#e8daef" : "#f39c12");
  const trackW = 2;
  const progW = 3.5;
  const start = -Math.PI / 2;
  const segs = BUILD_RING_SEGMENTS;
  for (let i = 0; i < segs; i++) {
    const a0 = start + (i / segs) * Math.PI * 2;
    const a1 = start + ((i + 1) / segs) * Math.PI * 2;
    ctx.drawLine(
      vec(center.x + radius * Math.cos(a0), center.y + radius * Math.sin(a0)),
      vec(center.x + radius * Math.cos(a1), center.y + radius * Math.sin(a1)),
      track,
      trackW,
    );
  }
  if (p <= 0) {
    return;
  }
  const progSegs = Math.max(2, Math.ceil(segs * p));
  const sweep = p * Math.PI * 2;
  for (let i = 0; i < progSegs; i++) {
    const a0 = start + (i / progSegs) * sweep;
    const a1 = start + ((i + 1) / progSegs) * sweep;
    ctx.drawLine(
      vec(center.x + radius * Math.cos(a0), center.y + radius * Math.sin(a0)),
      vec(center.x + radius * Math.cos(a1), center.y + radius * Math.sin(a1)),
      fill,
      progW,
    );
  }
};

/** 0–1: elapsed fraction of egg incubation (hatch when 1). */
const eggGrowthProgress = (st: InstanceType<typeof CellStateComponent>): number => {
  if (st.stage !== "egg") {
    return 0;
  }
  const total = COLONY.eggDurationMs;
  if (total <= 0) {
    return 0;
  }
  return 1 - Math.min(1, Math.max(0, st.eggTimerMs) / total);
};

/** 0–1: food delivered toward sealing (full when 1). */
const larvaeFeedingProgress = (st: InstanceType<typeof CellStateComponent>): number => {
  if (st.stage !== "larvae") {
    return 0;
  }
  const pollenTotal = COLONY.larvaePollenUnitsNeeded;
  const nectarTotal = COLONY.larvaeNectarUnitsNeeded;
  const total = pollenTotal + nectarTotal;
  if (total <= 0) {
    return 0;
  }
  const delivered =
    pollenTotal - st.larvaePollenRemaining + (nectarTotal - st.larvaeNectarRemaining);
  return Math.min(1, Math.max(0, delivered / total));
};

/** Outline when the pointer is over a hive hex (built cell or eligible empty slot). */
const HOVER_HEX_OUTLINE = Color.fromRGB(255, 255, 255, 0.72);
const HOVER_HEX_STROKE_PX = 1.75;

/** Subtle stroke for empty hexes where a foundation may be placed. */
// Brighter mint outline that contrasts with the green background but matches the game's accents.
const ELIGIBLE_BUILD_OUTLINE = Color.fromRGB(130, 224, 170, 0.75);
const ELIGIBLE_BUILD_STROKE_PX = 1.6;

const HONEY_LABEL_EPS = 1e-6;
const HONEY_FULL_EPS = 1e-3;

/** Fallback fill when cell art is not loaded yet. */
const colorForCellFallback = (st: InstanceType<typeof CellStateComponent>): Color => {
  if (!st.built) {
    return Color.fromHex("#7f8c8d");
  }
  if (st.stage === "foundation") {
    return Color.fromHex("#95a5a6");
  }
  switch (st.cellType) {
    case "brood":
      if (st.stage === "egg") {
        return Color.fromHex("#fdebd0");
      }
      if (st.stage === "larvae") {
        return Color.fromHex("#f8c471");
      }
      if (st.stage === "sealed") {
        return Color.fromHex("#d7bde2");
      }
      if (st.stage === "cleaning") {
        return Color.fromHex("#aed6f1");
      }
      return Color.fromHex("#fadbd8");
    case "pollen":
      return Color.fromHex("#f7dc6f");
    case "nectar":
      return Color.fromHex("#82e0aa");
    default:
      return Color.fromHex("#ecf0f1");
  }
};

/** Source art per frame in `/images/cell_sprites.png` (4×4 grid). */
const CELL_SPRITE_SRC_W = 246;
const CELL_SPRITE_SRC_H = 280;

/**
 * Scales isometric cell art to match the former filled-disc radius ({@link COLONY.hexSize} × 0.55).
 */
const CELL_SPRITE_DEST: { width: number; height: number } = (() => {
  const maxD = COLONY.hexSize * 2;
  const s = maxD / Math.max(CELL_SPRITE_SRC_W, CELL_SPRITE_SRC_H);
  return { width: CELL_SPRITE_SRC_W * s, height: CELL_SPRITE_SRC_H * s };
})();

const drawCellSpriteSheetFrame = (
  ctx: ExcaliburGraphicsContext,
  center: { x: number; y: number },
  /** 1-based index (top-left = 1, row-major). */
  frame1Based: number,
  opacity: number,
): void => {
  if (!cellSpritesImage.isLoaded()) {
    return;
  }
  const idx = Math.min(16, Math.max(1, Math.floor(frame1Based))) - 1;
  const col = idx % 4;
  const row = (idx / 4) | 0;
  const sx = col * CELL_SPRITE_SRC_W;
  const sy = row * CELL_SPRITE_SRC_H;
  const { width: dw, height: dh } = CELL_SPRITE_DEST;
  const dx = center.x - dw / 2;
  const dy = center.y - dh / 2;
  ctx.save();
  ctx.opacity = opacity;
  ctx.drawImage(
    cellSpritesImage.image,
    sx,
    sy,
    CELL_SPRITE_SRC_W,
    CELL_SPRITE_SRC_H,
    dx,
    dy,
    dw,
    dh,
  );
  ctx.restore();
};

/**
 * Draws the appropriate hive cell sprite(s) for {@link CellStateComponent}.
 * Foundation: frame 1 at 50% opacity → 100% by {@link CellStateComponent.buildProgress}.
 */
const drawCellInteriorFromState = (
  ctx: ExcaliburGraphicsContext,
  center: { x: number; y: number },
  st: InstanceType<typeof CellStateComponent>,
): void => {
  if (!cellSpritesImage.isLoaded()) {
    ctx.drawCircle(
      vec(center.x, center.y),
      COLONY.hexSize * 0.55,
      colorForCellFallback(st),
      Color.fromHex("#2c3e50"),
      1,
    );
    return;
  }

  if (!st.built) {
    const o = 0.25 + 0.75 * Math.min(1, Math.max(0, st.buildProgress));
    drawCellSpriteSheetFrame(ctx, center, 1, o);
    return;
  }

  if (st.cellType === "none" || st.stage === "foundation") {
    drawCellSpriteSheetFrame(ctx, center, 1, 1);
    return;
  }

  if (st.cellType === "brood") {
    if (st.stage === "empty") {
      drawCellSpriteSheetFrame(ctx, center, 1, 1);
      return;
    }
    if (st.stage === "egg") {
      drawCellSpriteSheetFrame(ctx, center, 11, 1);
      return;
    }
    if (st.stage === "larvae") {
      const p = larvaeFeedingProgress(st);
      const frame = p < 1 / 3 ? 12 : p < 2 / 3 ? 13 : 14;
      drawCellSpriteSheetFrame(ctx, center, frame, 1);
      return;
    }
    if (st.stage === "sealed") {
      drawCellSpriteSheetFrame(ctx, center, 15, 1);
      return;
    }
    if (st.stage === "cleaning") {
      drawCellSpriteSheetFrame(ctx, center, 16, 1);
      return;
    }
    drawCellSpriteSheetFrame(ctx, center, 1, 1);
    return;
  }

  if (st.cellType === "pollen") {
    if (st.pollenStored <= 0) {
      drawCellSpriteSheetFrame(ctx, center, 1, 1);
      return;
    }
    const r = st.pollenStored / COLONY.pollenCellCapacity;
    const frame = r < 1 / 3 ? 7 : r < 2 / 3 ? 8 : r < 1 ? 9 : 10;
    drawCellSpriteSheetFrame(ctx, center, frame, 1);
    return;
  }

  if (st.cellType === "nectar") {
    const proc = st.honeyProcessingProgress;
    if (proc > HONEY_LABEL_EPS && proc < 1 - HONEY_LABEL_EPS) {
      const t = Math.min(1, Math.max(0, proc));
      drawCellSpriteSheetFrame(ctx, center, 4, 1 - t);
      drawCellSpriteSheetFrame(ctx, center, 5, t);
      return;
    }
    if (st.honeyStored > HONEY_LABEL_EPS) {
      if (st.honeyStored >= COLONY.honeyCellCapacity - HONEY_FULL_EPS) {
        drawCellSpriteSheetFrame(ctx, center, 6, 1);
      } else {
        drawCellSpriteSheetFrame(ctx, center, 5, 1);
      }
      return;
    }
    const n = st.nectarStored;
    if (n <= 0) {
      drawCellSpriteSheetFrame(ctx, center, 1, 1);
      return;
    }
    const frac = n / COLONY.nectarCellCapacity;
    const frame = frac < 0.5 ? 2 : frac < 1 ? 3 : 4;
    drawCellSpriteSheetFrame(ctx, center, frame, 1);
    return;
  }

  drawCellSpriteSheetFrame(ctx, center, 1, 1);
};

const drawHexRing = (
  ctx: ExcaliburGraphicsContext,
  corners: [number, number][],
  color: Color,
  strokePx: number,
): void => {
  for (let i = 0; i < 6; i++) {
    const a = corners[i]!;
    const b = corners[(i + 1) % 6]!;
    ctx.drawLine(vec(a[0], a[1]), vec(b[0], b[1]), color, strokePx);
  }
};

/** Hex corner radius for eligible placement outlines and hover (matches former grid geometry). */
const HEX_CORNER_R_FACTOR = 0.92;

/**
 * Foundation placement eligibility for the active level (shared by cell draw passes).
 */
const getActiveLevelEligibleContext = (colony: ColonyRuntime) => {
  const lvl = colony.activeLevel;
  const lookup = {
    has: (k: string) => colony.cellsByKey.has(k),
    getBuilt: (k: string) => colony.cellsByKey.get(k)?.get(CellStateComponent),
  };
  const builtCoords: HiveCoord[] = [];
  for (const [, ent] of colony.cellsByKey) {
    const c = ent.get(CellCoordComponent)!;
    const st = ent.get(CellStateComponent)!;
    if (st.built) {
      builtCoords.push({ q: c.q, r: c.r, level: c.level });
    }
  }
  const eligibleForLevel = eligibleFoundationCoordsForLevel(lvl, lookup, builtCoords);
  const eligibleKeys = new Set(eligibleForLevel.map((c) => hiveKey(c)));
  return { lvl, eligibleForLevel, eligibleKeys };
};

/**
 * Draws hive cells for the active level (called from {@link Scene.onPreDraw}).
 */
export const drawHiveCells = (
  ctx: ExcaliburGraphicsContext,
  colony: ColonyRuntime,
): void => {
  const S = COLONY.hexSize;
  const { lvl, eligibleForLevel } = getActiveLevelEligibleContext(colony);
  for (const coord of eligibleForLevel) {
    const w = hexToWorld({ q: coord.q, r: coord.r }, S);
    const corners = hexCorners(w.x, w.y, S * HEX_CORNER_R_FACTOR);
    for (let i = 0; i < 6; i++) {
      const a = corners[i]!;
      const b = corners[(i + 1) % 6]!;
      ctx.drawLine(
        vec(a[0], a[1]),
        vec(b[0], b[1]),
        ELIGIBLE_BUILD_OUTLINE,
        ELIGIBLE_BUILD_STROKE_PX,
      );
    }
  }

  for (const [, ent] of colony.cellsByKey) {
    const c = ent.get(CellCoordComponent)!;
    if (c.level !== lvl) {
      continue;
    }
    const st = ent.get(CellStateComponent)!;
    const w = hexToWorld({ q: c.q, r: c.r }, S);
    drawCellInteriorFromState(ctx, w, st);
    if (!st.built) {
      drawBuildProgressRing(ctx, w, S * BUILD_RING_RADIUS_FACTOR, st.buildProgress);
    } else if (st.cellType === "brood") {
      const ringR = S * BUILD_RING_RADIUS_FACTOR;
      if (st.stage === "egg") {
        drawBroodGrowthRing(ctx, w, ringR, eggGrowthProgress(st), "egg");
      } else if (st.stage === "larvae") {
        drawBroodGrowthRing(ctx, w, ringR, larvaeFeedingProgress(st), "larvae");
      }
    }
  }
  colony.resourceDots.drawCellDots(ctx, colony);
};

/**
 * Hover ring for the active level. Runs in {@link Scene.onPostDraw} so it draws above bee actors.
 */
export const drawHiveCellOverlays = (
  ctx: ExcaliburGraphicsContext,
  colony: ColonyRuntime,
): void => {
  const key = colony.hoverHiveKey;
  if (!key) {
    return;
  }
  const S = COLONY.hexSize;
  const { lvl, eligibleKeys } = getActiveLevelEligibleContext(colony);
  const coord = parseHiveKey(key);
  if (coord.level !== lvl) {
    return;
  }
  if (!colony.cellsByKey.has(key) && !eligibleKeys.has(key)) {
    return;
  }
  const w = hexToWorld({ q: coord.q, r: coord.r }, S);
  const corners = hexCorners(w.x, w.y, S * HEX_CORNER_R_FACTOR);
  drawHexRing(ctx, corners, HOVER_HEX_OUTLINE, HOVER_HEX_STROKE_PX);
};
